import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Tables } from '@/integrations/supabase/types';
import {
  getCartPaymentOptions,
  getCartLineAmount,
  type CartPaymentOption,
} from '@/lib/cartPaymentOptions';

type Program = Tables<'programs'>;
type Child = Tables<'children'>;

export interface CartItem {
  program: Program;
  selectedChildren: Child[];
  /** Payment plan chosen in the cart (monthly, PAYG, full, etc.). */
  paymentOption?: CartPaymentOption;
  coupon?: {
    id: string;
    code: string;
    discount_type: string;
    discount_value: number;
    current_uses: number;
  };
  addedAt: string;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (program: Program) => void;
  removeFromCart: (programId: string) => void;
  updateCartItem: (programId: string, updates: Partial<CartItem>) => void;
  setPaymentOption: (programId: string, option: CartPaymentOption) => void;
  clearCart: () => void;
  isInCart: (programId: string) => boolean;
  cartCount: number;
  cartTotal: number;
  /** True when at least one program still needs a payment plan choice. */
  hasPendingPaymentSelection: boolean;
  isCartOpen: boolean;
  setIsCartOpen: (open: boolean) => void;
}


const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'msb_cart';

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setItems(parsed);
      } catch (e) {
        console.error('Failed to parse cart from storage:', e);
      }
    }
  }, []);

  // Persist cart to localStorage
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addToCart = (program: Program) => {
    if (isInCart(program.id)) return;

    // Auto-select when the program only offers one plan, so nothing is pending.
    const available = getCartPaymentOptions(program);

    setItems(prev => [...prev, {
      program,
      selectedChildren: [],
      paymentOption: available.length === 1 ? available[0].value : undefined,
      addedAt: new Date().toISOString(),
    }]);
    setIsCartOpen(true);
  };

  const removeFromCart = (programId: string) => {
    setItems(prev => prev.filter(item => item.program.id !== programId));
  };

  const updateCartItem = (programId: string, updates: Partial<CartItem>) => {
    setItems(prev => prev.map(item => 
      item.program.id === programId ? { ...item, ...updates } : item
    ));
  };

  const setPaymentOption = (programId: string, option: CartPaymentOption) => {
    updateCartItem(programId, { paymentOption: option });
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem(CART_STORAGE_KEY);
  };

  const isInCart = (programId: string) => {
    return items.some(item => item.program.id === programId);
  };

  const cartCount = items.length;

  // Pending only when the program genuinely offers more than one admin-enabled plan
  const hasPendingPaymentSelection = items.some(
    item => !item.paymentOption && getCartPaymentOptions(item.program).length > 1,
  );

  const cartTotal = items.reduce((total, item) => {
    const perChild = getCartLineAmount(item.program, item.paymentOption);
    // Pending or attendance-based (PAYG) lines contribute nothing to the subtotal.
    if (perChild === null) return total;

    const basePrice = perChild * Math.max(item.selectedChildren.length, 1);
    if (item.coupon) {
      if (item.coupon.discount_type === 'percentage') {
        return total + basePrice * (1 - item.coupon.discount_value / 100);
      } else {
        return total + Math.max(0, basePrice - item.coupon.discount_value);
      }
    }
    return total + basePrice;
  }, 0);

  return (
    <CartContext.Provider value={{
      items,
      addToCart,
      removeFromCart,
      updateCartItem,
      setPaymentOption,
      clearCart,
      isInCart,
      cartCount,
      cartTotal,
      hasPendingPaymentSelection,
      isCartOpen,
      setIsCartOpen,
    }}>

      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
